import type { ComputedRef } from "vue";
import type { Model } from "@/schemas/model.schema";
import { computed } from "vue";
import { isDeprecated } from "@/pages/agents/model-ratings";
import { useModelsQuery } from "@/queries/models.query";

/**
 * Modelos usados quando o catálogo ainda não resolveu (ou está vazio).
 * Devem existir sempre no catálogo de produção.
 */
export const FALLBACK_LLM_MODEL = "gpt-5-mini";
export const FALLBACK_TTS_MODEL = "openai-tts";

/**
 * Seleciona o modelo de maior prioridade dentre os elegíveis.
 * Empate: mantém o primeiro (a query já ordena por prioridade desc).
 */
function pickTopByPriority(models: Model[]): Model | undefined {
  return models.reduce<Model | undefined>((best, model) => {
    if (!best) return model;
    return (model.priority ?? 0) > (best.priority ?? 0) ? model : best;
  }, undefined);
}

/**
 * Expõe os modelos default resolvidos dinamicamente a partir do catálogo,
 * substituindo os antigos hardcodes ("gpt-5-mini"/"openai-tts") espalhados
 * pela criação de agentes e aplicação de templates.
 *
 * - `defaultLlmModel`: text-generation, active, não deprecado e com suporte a
 *   tools (agentes precisam de function calling), de maior prioridade.
 * - `defaultTtsModel`: text-to-speech, active, não deprecado, de maior prioridade.
 *
 * Retornam `undefined` enquanto a query não resolveu; o chamador deve cair no
 * fallback correspondente.
 */
export function useDefaultModels(): {
  defaultLlmModel: ComputedRef<string | undefined>;
  defaultTtsModel: ComputedRef<string | undefined>;
} {
  const modelsQuery = useModelsQuery();

  const defaultLlmModel = computed(() => {
    const eligible = modelsQuery.data.value.filter(
      (model) =>
        model.type === "text-generation" &&
        model.status === "active" &&
        !isDeprecated(model) &&
        model.capabilities?.tools === true
    );
    return pickTopByPriority(eligible)?.name;
  });

  const defaultTtsModel = computed(() => {
    const eligible = modelsQuery.data.value.filter(
      (model) =>
        model.type === "text-to-speech" && model.status === "active" && !isDeprecated(model)
    );
    return pickTopByPriority(eligible)?.name;
  });

  return { defaultLlmModel, defaultTtsModel };
}
